/**
 * Client-side profile API module — replaces profile.action.ts Server Actions.
 */

import { PROFILE } from "@/shared/constants/backend-api"
import { authFetchJson, getUserIdFromToken } from "@/shared/lib/auth-fetch"
import { ApiResponse } from "@/shared/types/backend-response.type"
import { Profile } from "@/features/auth/types/auth-actions.type"
import { getNonHomeownerUserById } from "@/features/volunteers/api/volunteers.api"
import { mapNonHomeownerUserToProfile } from "../lib/map-volunteer-to-profile"
import { UpdateAboutMeForm } from "../schemas/profile-form.schema"
import { ProfileLocationForm } from "../schemas/profile-location-form.schema"
import { StmInformationFormSchema } from "../schemas/stm-information-form.schema"
import { ProfileBaseInformationFormType } from "../schemas/profile-base-information-form.schema"
import { objectToFormData } from "@/shared/lib/utils"

// ---------------------------------------------------------------------------
// Query keys
// ---------------------------------------------------------------------------

export const PROFILE_QUERY_KEYS = {
  all: ["profile"] as const,
  detail: (userId: number) => ["profile", userId] as const,
  images: (userId: number) => ["profile", "images", userId] as const,
}

// ---------------------------------------------------------------------------
// Read
// ---------------------------------------------------------------------------

export async function getProfile(userId?: number): Promise<ApiResponse<Profile>> {
  const uid = userId !== undefined ? userId : getUserIdFromToken()
  return authFetchJson<Profile>(PROFILE.base(uid))
}

export async function getVolunteerProfile(
  userId?: number,
): Promise<{ success: true; data: Profile } | { success: false; message: string }> {
  const uid = userId !== undefined ? userId : getUserIdFromToken()
  const res = await getNonHomeownerUserById(uid)
  if (!res.success || !res.data) {
    return { success: false, message: !res.success ? res.message : "Failed to load profile" }
  }
  const profile = mapNonHomeownerUserToProfile(res.data)
  return { success: true, data: profile }
}

// ---------------------------------------------------------------------------
// Update
// ---------------------------------------------------------------------------

export async function updateProfile(
  userIdOrData?: number | UpdateAboutMeForm,
  data?: UpdateAboutMeForm,
): Promise<ApiResponse<UpdateAboutMeForm>> {
  let uid: number
  let finalData: UpdateAboutMeForm
  if (typeof userIdOrData === "object" || userIdOrData === undefined) {
    uid = getUserIdFromToken()
    finalData = userIdOrData || {} as UpdateAboutMeForm
  } else {
    uid = userIdOrData
    finalData = data!
  }
  return authFetchJson<UpdateAboutMeForm>(PROFILE.base(uid), {
    method: "PUT",
    body: JSON.stringify(finalData),
  })
}

export async function createProfileLocation(
  userIdOrData?: number | ProfileLocationForm,
  data?: ProfileLocationForm,
): Promise<ApiResponse<any>> {
  let uid: number
  let finalData: ProfileLocationForm
  if (typeof userIdOrData === "object" || userIdOrData === undefined) {
    uid = getUserIdFromToken()
    finalData = userIdOrData || {} as ProfileLocationForm
  } else {
    uid = userIdOrData
    finalData = data!
  }
  const formData = objectToFormData(finalData)
  return authFetchJson<any>(PROFILE.location(uid), {
    method: "POST",
    body: formData,
  })
}

export async function createProfileStm(
  userIdOrData?: number | StmInformationFormSchema,
  data?: StmInformationFormSchema,
): Promise<ApiResponse<StmInformationFormSchema>> {
  let uid: number
  let finalData: StmInformationFormSchema
  if (typeof userIdOrData === "object" || userIdOrData === undefined) {
    uid = getUserIdFromToken()
    finalData = userIdOrData || {} as StmInformationFormSchema
  } else {
    uid = userIdOrData
    finalData = data!
  }
  return authFetchJson<StmInformationFormSchema>(PROFILE.stm(uid), {
    method: "PUT",
    body: JSON.stringify(finalData),
  })
}

export async function createProfileBaseInformation(
  userIdOrData?: number | ProfileBaseInformationFormType,
  data?: ProfileBaseInformationFormType,
): Promise<ApiResponse<ProfileBaseInformationFormType>> {
  let uid: number
  let finalData: ProfileBaseInformationFormType
  if (typeof userIdOrData === "object" || userIdOrData === undefined) {
    uid = getUserIdFromToken()
    finalData = userIdOrData || {} as ProfileBaseInformationFormType
  } else {
    uid = userIdOrData
    finalData = data!
  }
  const formData = objectToFormData(finalData)
  return authFetchJson<ProfileBaseInformationFormType>(PROFILE.baseProfile(uid), {
    method: "POST",
    body: formData,
  })
}

export async function uploadProfilePicture(
  userIdOrFile: number | File,
  file?: File,
): Promise<ApiResponse<any>> {
  let uid: number
  let finalFile: File
  if (userIdOrFile instanceof File) {
    uid = getUserIdFromToken()
    finalFile = userIdOrFile
  } else {
    uid = userIdOrFile
    finalFile = file!
  }
  const formData = new FormData()
  formData.append("profilePicture", finalFile)
  return authFetchJson<any>(`${PROFILE.base(uid)}/profile-picture`, {
    method: "POST",
    body: formData,
  })
}

export async function updateSelectedRegion(
  userIdOrRegionId: number | string,
  selectedRegionId?: string | number,
): Promise<ApiResponse<Profile>> {
  let uid: number
  let regionId: string | number
  if (selectedRegionId === undefined) {
    uid = getUserIdFromToken()
    regionId = userIdOrRegionId
  } else {
    uid = userIdOrRegionId as number
    regionId = selectedRegionId
  }
  const regionIdNumber = typeof regionId === "string" ? parseInt(regionId, 10) : regionId
  if (isNaN(regionIdNumber)) throw new Error("Invalid region ID")

  return authFetchJson<Profile>(PROFILE.base(uid), {
    method: "PUT",
    body: JSON.stringify({ selectedRegion: regionIdNumber }),
  })
}

export async function updateProfileWithGoogleSignupInfo(
  userId: number,
  data: { fullname?: string; phone?: string },
): Promise<{ success: boolean; message?: string }> {
  const updateData: Record<string, string> = {}
  if (data.fullname?.trim()) updateData.fullname = data.fullname.trim()
  if (data.phone?.trim()) updateData.phone = data.phone.trim()

  if (!updateData.fullname && !updateData.phone) {
    return { success: false, message: "No valid data to update" }
  }

  const res = await authFetchJson<Profile>(PROFILE.base(userId), {
    method: "PUT",
    body: JSON.stringify(updateData),
  })
  return res.success ? { success: true } : { success: false, message: res.message }
}

// ---------------------------------------------------------------------------
// Delete
// ---------------------------------------------------------------------------

export async function deleteProfilePicture(userId?: number): Promise<ApiResponse<{ profilePicture: null }>> {
  const uid = userId !== undefined ? userId : getUserIdFromToken()
  return authFetchJson<{ profilePicture: null }>(`${PROFILE.base(uid)}/profile-picture`, {
    method: "DELETE",
  })
}

export async function deleteLocationImage(
  userIdOrUrl: number | string,
  imageUrl?: string,
): Promise<ApiResponse<{ locationImages: string[] }>> {
  let uid: number
  let url: string
  if (typeof userIdOrUrl === "string") {
    uid = getUserIdFromToken()
    url = userIdOrUrl
  } else {
    uid = userIdOrUrl
    url = imageUrl!
  }
  const encodedImageUrl = encodeURIComponent(url)
  return authFetchJson<{ locationImages: string[] }>(`${PROFILE.location(uid)}/${encodedImageUrl}`, {
    method: "DELETE",
  })
}

export async function deleteBaseImage(
  userIdOrUrl: number | string,
  imageUrl?: string,
): Promise<ApiResponse<{ baseImages: string[] }>> {
  let uid: number
  let url: string
  if (typeof userIdOrUrl === "string") {
    uid = getUserIdFromToken()
    url = userIdOrUrl
  } else {
    uid = userIdOrUrl
    url = imageUrl!
  }
  const encodedImageUrl = encodeURIComponent(url)
  return authFetchJson<{ baseImages: string[] }>(`${PROFILE.baseProfile(uid)}/${encodedImageUrl}`, {
    method: "DELETE",
  })
}
